<template>
  <div class="site-work-monthly-results">
    <b-container fluid>
      <b-row class="mb-2">
        <b-col>
          <BackToListLink />
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <h2>歩掛（月次実績）</h2>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col md="6">
          <b-form-group label="現場" label-for="site-select">
            <b-form-select
              id="site-select"
              v-model="selectedSiteId"
              :options="siteOptions"
              @change="onSiteChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="対象月" label-for="target-month">
            <b-form-input
              id="target-month"
              v-model="targetMonth"
              type="month"
              @change="onMonthChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-card class="mb-4" title="月次実績入力">
        <b-form v-if="isManagedUser" @submit.prevent="saveResult">
          <b-table-simple bordered small responsive head-variant="light">
            <b-thead>
              <b-tr>
                <b-th class="text-center" style="width: 25%;">工種</b-th>
                <b-th class="text-center" style="width: 15%;">日数</b-th>
                <b-th class="text-center" style="width: 15%;">出面(人)</b-th>
                <b-th class="text-center" style="width: 15%;">数量</b-th>
                <b-th class="text-center" style="width: 15%;">単位</b-th>
                <b-th class="text-center" style="width: 15%;">操作</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr>
                <b-td>
                  <b-form-select
                    id="result-work-item"
                    v-model="resultForm.siteWorkItemId"
                    :options="siteWorkItemOptions"
                  />
                </b-td>
                <b-td>
                  <b-form-input
                    id="result-days"
                    v-model="resultForm.days"
                    type="number"
                    min="0"
                  />
                </b-td>
                <b-td>
                  <b-form-input
                    id="result-workers"
                    v-model="resultForm.workers"
                    type="number"
                    min="0"
                  />
                </b-td>
                <b-td>
                  <b-form-input
                    id="result-quantity"
                    v-model="resultForm.quantity"
                    type="number"
                    step="0.01"
                  />
                </b-td>
                <b-td>
                  <b-form-input
                    id="result-unit"
                    v-model="resultForm.unit"
                  />
                </b-td>
                <b-td>
                  <b-button type="submit" variant="primary" size="sm">
                    {{ resultForm.id ? '更新' : '保存' }}
                  </b-button>
                  <b-button v-if="resultForm.id" @click="cancelEdit" variant="secondary" size="sm" class="ml-1">
                    キャンセル
                  </b-button>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-form>

        <b-table
          class="mt-3"
          :items="monthlyResults"
          :fields="resultFields"
          striped
          hover
          responsive
        >
          <template #cell(workType)="row">
            {{ getWorkTypeName(row.item.siteWorkItemId) }}
          </template>
          <template #cell(days)="row">
            {{ row.item.days ?? '-' }}
          </template>
          <template #cell(workers)="row">
            {{ row.item.workers ?? '-' }}
          </template>
          <template #cell(quantity)="row">
            {{ row.item.quantity ?? '-' }}
          </template>
          <template #cell(unit)="row">
            {{ row.item.unit || '-' }}
          </template>
          <template #cell(quantityPerWorker)="row">
            {{ formatQuantityPerWorker(row.item.quantityPerWorker, row.item.unit) }}
          </template>
          <template #cell(quantityPerDay)="row">
            {{ formatQuantityPerDay(row.item.quantityPerDay, row.item.unit) }}
          </template>
          <template #cell(actions)="row">
            <b-button v-if="isManagedUser" size="sm" @click="editResult(row.item)" variant="info" class="mr-1">
              編集
            </b-button>
            <b-button v-if="isManagedUser" size="sm" @click="deleteResult(row.item)" variant="danger">
              削除
            </b-button>
          </template>
        </b-table>

        <div v-if="monthlyResults.length === 0" class="text-center text-muted py-4">
          この月の実績データはまだ登録されていません
        </div>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import SiteWorkMonthlyResultApi from '@/api/SiteWorkMonthlyResultApi'
import SiteWorkItemApi from '@/api/SiteWorkItemApi'
import DiaryListConstants from '@/store/constants/diaryListRecords'
import BackToListLink from '@/components/shared/BackToListLink'

export default {
  name: 'SiteWorkMonthlyResults',
  components: { BackToListLink },
  data() {
    return {
      selectedSiteId: null,
      targetMonth: this.getCurrentMonth(),
      siteWorkItems: [],
      monthlyResults: [],
      resultForm: {
        id: null,
        siteWorkItemId: null,
        days: null,
        workers: null,
        quantity: null,
        unit: ''
      },
      resultFields: [
        { key: 'workType', label: '工種' },
        { key: 'days', label: '日数' },
        { key: 'workers', label: '出面(人)' },
        { key: 'quantity', label: '数量' },
        { key: 'unit', label: '単位' },
        { key: 'quantityPerWorker', label: '1人当たり数量' },
        { key: 'quantityPerDay', label: '日当たり数量' },
        { key: 'actions', label: '操作' }
      ]
    }
  },
  computed: {
    sites() {
      return this.$store.getters[DiaryListConstants.GETTERS.getSites] || []
    },
    siteOptions() {
      return [
        { value: null, text: '現場を選択してください' },
        ...this.sites.map(site => ({
          value: site.id,
          text: site.name
        }))
      ]
    },
    siteWorkItemOptions() {
      return [
        { value: null, text: '工種を選択してください' },
        ...this.siteWorkItems.map(item => ({
          value: item.id,
          text: item.workType
        }))
      ]
    },
    isManagedUser() {
      return !localStorage.getItem('contractor_token')
    }
  },
  async mounted() {
    await this.$store.dispatch(DiaryListConstants.DISPATCH.getSiteRecords)
    
    const siteId = this.$route.query.siteId
    if (siteId) {
      this.selectedSiteId = siteId
      await this.loadSiteWorkItems()
      if (this.targetMonth) {
        await this.loadMonthlyResults()
      }
    }
  },
  methods: {
    getCurrentMonth() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    async loadSiteWorkItems() {
      if (!this.selectedSiteId) {
        this.siteWorkItems = []
        return
      }
      try {
        const response = await SiteWorkItemApi.search({ siteId: this.selectedSiteId })
        this.siteWorkItems = response.data.siteWorkItems || []
      } catch (error) {
        console.error('Failed to load site work items:', error)
        this.$bvToast.toast('工種マスタの読み込みに失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    async loadMonthlyResults() {
      if (!this.selectedSiteId || !this.targetMonth) {
        this.monthlyResults = []
        return
      }
      try {
        const response = await SiteWorkMonthlyResultApi.search({
          siteId: this.selectedSiteId,
          yearMonth: this.targetMonth
        })
        this.monthlyResults = response.data.siteWorkMonthlyResults || []
      } catch (error) {
        console.error('Failed to load monthly results:', error)
        this.$bvToast.toast('月次実績の読み込みに失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    async onSiteChange() {
      await this.loadSiteWorkItems()
      if (this.selectedSiteId && this.targetMonth) {
        await this.loadMonthlyResults()
      }
    },
    async onMonthChange() {
      if (this.selectedSiteId && this.targetMonth) {
        await this.loadMonthlyResults()
      }
    },
    getWorkTypeName(siteWorkItemId) {
      const item = this.siteWorkItems.find(i => i.id === siteWorkItemId)
      return item ? item.workType : '-'
    },
    formatQuantityPerWorker(value, unit) {
      if (value === undefined || value === null) return '-'
      const formattedValue = Number(value).toLocaleString('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      const unitDisplay = unit || '単位'
      return `${formattedValue}${unitDisplay}/人`
    },
    formatQuantityPerDay(value, unit) {
      if (value === undefined || value === null) return '-'
      const formattedValue = Number(value).toLocaleString('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      const unitDisplay = unit || '単位'
      return `${formattedValue}${unitDisplay}/日`
    },
    async saveResult() {
      if (!this.selectedSiteId || !this.targetMonth) {
        this.$bvToast.toast('現場と対象月を選択してください', {
          title: 'エラー',
          variant: 'danger'
        })
        return
      }
      if (!this.resultForm.siteWorkItemId) {
        this.$bvToast.toast('工種を選択してください', {
          title: 'エラー',
          variant: 'danger'
        })
        return
      }

      try {
        if (this.resultForm.id) {
          await SiteWorkMonthlyResultApi.update({
            id: this.resultForm.id,
            siteId: this.selectedSiteId,
            siteWorkItemId: this.resultForm.siteWorkItemId,
            yearMonth: this.targetMonth,
            days: this.resultForm.days ? parseInt(this.resultForm.days, 10) : null,
            workers: this.resultForm.workers ? parseInt(this.resultForm.workers, 10) : null,
            quantity: this.resultForm.quantity ? parseFloat(this.resultForm.quantity) : null,
            unit: this.resultForm.unit || null
          })
          this.$bvToast.toast('月次実績を更新しました', {
            title: '成功',
            variant: 'success'
          })
        } else {
          await SiteWorkMonthlyResultApi.create({
            siteId: this.selectedSiteId,
            siteWorkItemId: this.resultForm.siteWorkItemId,
            yearMonth: this.targetMonth,
            days: this.resultForm.days ? parseInt(this.resultForm.days, 10) : null,
            workers: this.resultForm.workers ? parseInt(this.resultForm.workers, 10) : null,
            quantity: this.resultForm.quantity ? parseFloat(this.resultForm.quantity) : null,
            unit: this.resultForm.unit || null
          })
          this.$bvToast.toast('月次実績を保存しました', {
            title: '成功',
            variant: 'success'
          })
        }
        this.resetForm()
        await this.loadMonthlyResults()
      } catch (error) {
        console.error('Failed to save monthly result:', error)
        this.$bvToast.toast('月次実績の保存に失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    editResult(item) {
      this.resultForm = {
        id: item.id,
        siteWorkItemId: item.siteWorkItemId,
        days: item.days,
        workers: item.workers,
        quantity: item.quantity,
        unit: item.unit || ''
      }
    },
    async deleteResult(item) {
      if (!confirm('この実績を削除しますか？')) {
        return
      }
      try {
        await SiteWorkMonthlyResultApi.delete({ id: item.id })
        this.$bvToast.toast('月次実績を削除しました', {
          title: '成功',
          variant: 'success'
        })
        await this.loadMonthlyResults()
      } catch (error) {
        console.error('Failed to delete monthly result:', error)
        this.$bvToast.toast('月次実績の削除に失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    cancelEdit() {
      this.resetForm()
    },
    resetForm() {
      this.resultForm = {
        id: null,
        siteWorkItemId: null,
        days: null,
        workers: null,
        quantity: null,
        unit: ''
      }
    }
  }
}
</script>

<style scoped>
.site-work-monthly-results {
  padding: 20px;
}
</style>
